<template>
  <div>
    <b-sidebar
      id="sidebar-digital-franchise-purchase"
      sidebar-class="sidebar-lg"
      :visible="digitalFranchisePurchaseSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @shown="onShow"
      @change="(val) => mutateDigitalFranchisePurchaseSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h4>Compra de franquia</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <!-- Form -->
        <div v-if="loading.form" class="justify-content-center align-items-center">
          <b-spinner
            class="my-3 mx-auto d-block"
            variant="primary"
          />
        </div>
        <b-form
          v-else
          class="p-2"
          @submit.prevent="onSubmit"
          @reset.prevent="resetForm"
        >
          <b-row>
            <b-col cols="12">
              <b-form-group 
                label="PN" 
                label-for="digital-franchise-consultant-pn"
                class="mt-1"
              >
                <v-select
                  id="digital-franchise-consultant-pn"
                  v-model="consultant"
                  :reduce="(consultants) => consultants.id"
                  :options="consultants"
                  :loading="loading.consultants"
                  :disabled="loading.consultants || consultantDisabled"
                  label="name"
                  placeholder="Selecione o consultor"
                  :class="getSelectErrorClass(v$.consultant.$error)"
                />
                <div class="invalid-feedback" :class="{ 'd-block': v$.consultant.$error }">
                  <span v-if="v$.consultant.required.$invalid">
                    Você deve informar um consultor.
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12">
              <b-form-group 
                label="Tipo de franquia" 
                label-for="digital-franchise-type"
                class="mt-1"
              >
                <v-select
                  id="digital-franchise-type"
                  v-model="franchise"
                  :reduce="(franchises) => franchises.key"
                  :options="franchiseOptions"
                  :loading="loading.franchiseTypes"
                  :disabled="loading.franchiseTypes"
                  label="name"
                  placeholder="Selecione o tipo de franquia"
                  :class="getSelectErrorClass(v$.franchise.$error)"
                />
                <div class="invalid-feedback" :class="{ 'd-block': v$.franchise.$error }">
                  <span v-if="v$.franchise.required.$invalid">
                    Você deve informar o tipo de franquia.
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <template v-if="isPendingApproval">
            <b-row>
              <b-col cols="12">
                <b-form-group label="Valor" label-for="amount">
                  <b-form-input
                    id="amount"
                    v-model="amount"
                    v-money="moneyFormat"
                    :class="{ 'is-invalid': v$.amount.$error }"
                  />
                  <div class="invalid-feedback">
                    <span v-if="v$.amount.required.$invalid || v$.amount.minValue.$invalid">
                      Você deve informar um valor válido maior que zero
                    </span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <b-form-group 
                  label="Método de pagamento" 
                  label-for="digital-franchise-payment-method"
                  class="mt-1"
                >
                  <b-form-textarea
                    id="digital-franchise-payment-method"
                    v-model="payment_format"
                    max-rows="6"
                    :class="{ 'is-invalid': v$.payment_format.$error }"
                  />
                  <div class="invalid-feedback">
                    <span v-if="v$.payment_format.required.$invalid">
                      Você deve informar a forma de pagamento.
                    </span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>
          </template>
          <!-- Form Actions -->
          <div class="d-flex mt-2 justify-content-between">
            <template v-if="!isPendingApproval">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :disabled="saving"
                variant="outline-secondary"
                type="reset"
                class="payment-buttons"
              >
                Cancelar
              </b-button>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :disabled="saving"
                variant="primary"
                type="submit"
                class="payment-buttons"
              >
                {{ saving ? "Aguarde..." : "Salvar e enviar para análise" }}
              </b-button>
            </template>
            <template v-else>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :disabled="saving"
                variant="outline-danger"
                class="payment-buttons"
                @click="reject"
              >
                 {{ saving ? "Aguarde..." : "Rejeitar" }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :disabled="saving"
                variant="success"
                class="payment-buttons"
                @click="approve"
              >
                {{ saving ? "Aguarde..." : "Aprovar" }}
              </b-button>
            </template>
          </div>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BButton, BFormInput, BFormTextarea, BCol, BRow, BSpinner
} from 'bootstrap-vue'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import useVuelidate from "@vuelidate/core"
import { minValue, required, requiredIf } from "@vuelidate/validators"
import Ripple from 'vue-ripple-directive'
import vSelect from "vue-select";
import * as types from '../store/types'
import { getVueSelectErrorClass } from '@/helpers/validators'
import { FRANQUEADO_EXPANSAO, FRANQUEADO_PLANEJADOR } from '@/constants/digital_franchise_purchase_types'
import * as consultantTypes from "@/constants/consultants";
import * as sharedTypes from '@/modules/shared/store/types'
import _ from 'lodash'
import consultant from '@/router/routes/consultant'
import { getBRLFormat } from '@/helpers/formatting'
import { VMoney } from "v-money";
import { floatToMoney, moneyToFloat } from '@/helpers/converters'

export default {
  components: {
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BCol,
    BRow,
    BSpinner,
    vSelect,
  },
  props: {
    callback: Function,
  },
  directives: { 
    Ripple,
    money: VMoney
  },
  data() {
    return {
      loading: {
        form: false,
        franchiseTypes: false,
        consultants: false,
      },
      consultantDisabled: true,
      consultant: undefined,
      franchise: undefined,
      amount: undefined,
      payment_format: undefined,
      status: undefined,
      currentStatus: undefined,
      saving: false,
      isApproval: false
    }
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() };
  },
  validations() {
    return {
      consultant: { required },
      franchise: { required },
      amount: { 
        required: requiredIf(() => {
          return this.isApproval
        }),
        minValue: (value) => {
          if(this.isApproval){
            return moneyToFloat(value) > 0;
          }
          return true;
        }
      },
      payment_format: { required: requiredIf(() => {
        return this.isApproval
      })}
    };
  },
  computed: {
    ...mapGetters({
      digitalFranchisePurchaseSidebar: types.DIGITAL_FRANCHISE_PURCHASE_SIDEBAR,
      franchisesTypes: types.DIGITAL_FRANCHISE_PURCHASE_FRANCHISE_TYPES,
      consultants: sharedTypes.CONSULTANTS,
    }),
    franchiseOptions(){
      if(this.franchisesTypes && this.franchisesTypes.length > 0){
        const consultantSelected = _.find(this.consultants, c => c.id == this.consultant);
        if(consultantSelected && 
          (consultantTypes.isUserPlanejador(consultantSelected.user_role_id) ||
          consultantTypes.isUserExpansao(consultantSelected.user_role_id))
        ){
          return _.filter(this.franchisesTypes, ft => ft.key != FRANQUEADO_PLANEJADOR)
        }else{ 
          return _.filter(this.franchisesTypes, ft => ft.key != FRANQUEADO_EXPANSAO)
        }
      }
      return this.franchisesTypes;
    },
    moneyFormat: function () {
      return getBRLFormat();
    },
    isPendingApproval(){
      return this.currentStatus === 'pending'
    },
    consultantOptions(){
      return _.map(this.consultants, c => ({
        ...c,
        name: `${c.name} - ${c.role}`
      }) );
    }
  },
  mounted() {
    this.loading.franchiseTypes = true;
    this.getFranchiseTypes()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os tipos de franquia para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.franchiseTypes = false;
      });
    this.loading.consultants = true;
    this.getConsultants()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os consultores para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.consultants = false;
      });
  },
  methods: {
    ...mapMutations({
      mutateDigitalFranchisePurchaseSidebar: types.MUTATE_DIGITAL_FRANCHISE_PURCHASE_SIDEBAR
    }),
    ...mapActions({
      saveFranchisePurchase: types.SAVE_DIGITAL_FRANCHISE_PURCHASE,
      updateFranchisePurchase: types.UPDATE_DIGITAL_FRANCHISE_PURCHASE,
      getFranchiseTypes: types.GET_DIGITAL_FRANCHISE_PURCHASE_FRANCHISE_TYPES,
      getConsultants: sharedTypes.GET_CONSULTANTS,
      getDigitalFranchisePurchase: types.GET_DIGITAL_FRANCHISE_PURCHASE,
    }),
    onShow() {
      this.saving = false;
      if(this.digitalFranchisePurchaseSidebar.consultant_id){
        this.consultant = this.digitalFranchisePurchaseSidebar.consultant_id;
      }else{
        this.consultant = undefined;
        this.consultantDisabled = false;
      }
      if(this.digitalFranchisePurchaseSidebar.id){
        this.loading.form = true
        this.getDigitalFranchisePurchase(this.digitalFranchisePurchaseSidebar.id)
          .then((resp) => {
            if(resp.data.status !== 'pending'){
              this.toast({
                component: ToastificationContent,
                props: {
                  title: "Oops!",
                  text: "Não é possível editar uma proposta de compra que já foi analisada anteriormente.",
                  icon: "AlertTriangleIcon",
                  variant: "warning",
                },
              });
              this.mutateDigitalFranchisePurchaseSidebar({ visible: false });
              return;
            }
            this.consultant = resp.data.consultant_id;
            this.consultantDisabled = true;
            this.franchise = resp.data.franchise_type;
            this.amount = floatToMoney(resp.data.amount);
            this.payment_format = resp.data.payment_format;
            this.currentStatus = resp.data.status;
          })
          .catch(() => {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: "Ocorreu um erro ao carregar os dados da proposta de compra. Entre em contato com o setor de TI.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          })
          .finally(() => {
            this.loading.form = false;
          });
      }
    },
    approve() {
      this.isApproval = true;
      this.status = 'approved';
      this.onSubmit();
    },
    reject() {
      this.status = 'rejected';
      this.isApproval = false;
      this.amount = undefined;
      this.payment_format = undefined;
      this.onSubmit();
    },
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;
      this.saving = true;
      const payload = {
        id: this.digitalFranchisePurchaseSidebar.id,
        consultant_id: this.consultant,
        franchise_type: this.franchise,
        amount: this.amount? moneyToFloat(this.amount):undefined,
        payment_format: this.payment_format,
        status: this.status,
      };

      const savePurchase = this.status? this.updateFranchisePurchase: this.saveFranchisePurchase

      savePurchase(payload)
        .then(() => {
          var message = "Proposta de compra de franquia salva e enviada para análise com sucesso!"
          if(this.status === 'approved' ){
            message = "Proposta de compra de franquia aprovada com sucesso!"
          }else if(this.status === 'rejected'){
            message = "Proposta de compra de franquia rejeitada com sucesso!"
          }
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso!",
              text: message,
              icon: "CheckIcon",
              variant: "success",
            },
          });
          this.mutateDigitalFranchisePurchaseSidebar({ visible: false });
          if (this.digitalFranchisePurchaseSidebar.saveAction) {
            this.digitalFranchisePurchaseSidebar.saveAction();
          }
        })
        .catch((err) => {
          if (err?.response?.status == 400) {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: err.response.data.error,
                icon: "AlertTriangleIcon",
                variant: "warning",
              },
            });
          } else {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: "Ocorreu um erro ao salvar a proposta de compra. Entre em contato com o setor de TI.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError);
    },
    clear() {
      this.franchise = undefined;
      this.consultant = undefined;
      this.consultantDisabled = true;
      this.amount = undefined;
      this.payment_format = undefined;
      this.status = undefined;
      this.currentStatus = undefined;
      this.isApproval = false;
      this.mutateDigitalFranchisePurchaseSidebar({ 
        id: undefined, 
        consultant_id: undefined,
      });
      this.v$.$reset();
    },
    resetForm() {
      this.mutateDigitalFranchisePurchaseSidebar({ visible: false });
      this.clear();
    },
  }
}
</script>

<style lang="scss">
.sidebar-bottom {
  position: absolute;
  bottom: 0;
  width: -moz-available;          /* WebKit-based browsers will ignore this. */
  width: -webkit-fill-available;  /* Mozilla-based browsers will ignore this. */
  width: fill-available;
}

.payment-buttons {
  width: 11rem;
}
</style>
